import type { NotificationEvent } from '../types/notifications.types';

/**
 * Compares two Redis Stream ids of the form `<ms>-<seq>` numerically.
 * Negative when `a < b`, positive when `a > b`, 0 when equal. Both
 * components are compared numerically (each fits a JS number for any
 * realistic stream age). A malformed/empty id sorts BEFORE any
 * well-formed id so it never blocks a live frame from advancing the
 * cursor.
 */
export function compareStreamIds(a: string, b: string): number {
  if (a === b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const [ams, aseq] = a.split('-');
  const [bms, bseq] = b.split('-');
  const amsN = Number(ams);
  const bmsN = Number(bms);
  if (amsN !== bmsN) return amsN - bmsN;
  return Number(aseq || 0) - Number(bseq || 0);
}

/**
 * Canonical newest-first sort for the notifications feed.
 *
 * PRIMARY key is `occurredAt` (event time — what the operator reads in the
 * timestamp column), descending. This is the ONLY axis shared by BOTH the
 * live overlay AND the REST history, so the two merge cleanly with no
 * "ascending run" at the live/REST seam.
 *
 * Same-timestamp ties are broken by the global `streamId` (the Redis Stream
 * id `<ms>-<seq>`, a monotonic total order ACROSS rules) when both rows
 * carry it, so same-millisecond events from different rules order
 * deterministically. REST-only rows (no streamId) and any pre-streamId
 * event fall back to per-rule `seq` desc, a null seq sorting AFTER a numeric
 * one (mirrors Mongo's descending order).
 *
 * NOTE: streamId is deliberately NOT the primary key — it reflects Redis
 * INGEST order, which can lag event time and would make the timestamp column
 * look out of order. It is only a same-timestamp tiebreak.
 */
export function compareNotificationsDesc(
  a: NotificationEvent,
  b: NotificationEvent,
): number {
  const at = new Date(a.occurredAt).getTime();
  const bt = new Date(b.occurredAt).getTime();
  if (at !== bt) return bt - at;
  if (a.streamId && b.streamId) {
    const cmp = compareStreamIds(b.streamId, a.streamId); // desc
    if (cmp !== 0) return cmp;
  }
  const aseq = a.seq;
  const bseq = b.seq;
  if (aseq == null && bseq == null) return 0;
  if (aseq == null) return 1; // null sorts AFTER numeric in desc
  if (bseq == null) return -1;
  return bseq - aseq;
}

/**
 * Insert `evt` into `arr` (already sorted newest-first by `compare`) IN PLACE
 * so the array stays sorted. Walks from the top and stops at the first row
 * `evt` sorts before-or-equal to — i.e. the first `compare(evt, arr[i]) <= 0`.
 *
 * 🚨 The stop condition is the crux: `compare(a, b) <= 0` means `a` is
 * newer-or-equal and sorts FIRST (same convention `mergeNotificationFeed`
 * relies on). The previous live-overlay insert had this inverted (`continue`
 * on `<= 0`), so every NEW frame skipped past all newer rows and landed at the
 * BOTTOM — the live block rendered oldest-first (ascending) above the
 * descending REST history.
 */
export function insertDescending(
  arr: NotificationEvent[],
  evt: NotificationEvent,
  compare: (a: NotificationEvent, b: NotificationEvent) => number,
): void {
  let i = 0;
  while (i < arr.length && compare(evt, arr[i]) > 0) i += 1;
  arr.splice(i, 0, evt);
}

/**
 * Hard ceiling on how many notification rows the feed keeps in memory.
 *
 * The page is a live, top-anchored feed. `useInfiniteQuery` runs on
 * react-query v3 (no built-in `maxPages`), so `data.pages` would grow
 * without bound as "Load older" is clicked, while the live overlay
 * triggers a feed rebuild on every ~100ms coalesce tick. Capping the
 * MERGED feed bounds, in one place, the virtualizer count, its DOM
 * measurement cache, the per-tick rebuild cost, and the retained-object
 * set. Older rows stay in Mongo and are reachable via the time-range /
 * Pair / Rule filters — the feed's primary "go back" controls.
 */
export const MAX_FEED_ROWS = 1000;

/**
 * Merge the live WS overlay with the react-query REST pages into a single
 * newest-first feed, deduped by `id` and capped at `max` rows.
 *
 * Both inputs are ALREADY sorted descending by the page's canonical key
 * (occurredAt desc, then per-rule `seq` desc): the overlay maintains that
 * order via ordered insert, and each REST page comes back desc with pages
 * appended newest→older. So this is a linear two-way merge rather than a
 * full re-sort of the whole combined array on every live tick — the old
 * hot-path cost that grew with every loaded page.
 *
 * On an id collision (an event delivered via BOTH the realtime channel and
 * a REST refetch) the overlay copy wins, preserving the previous
 * "live overlay first" dedupe.
 */
export function mergeNotificationFeed(
  overlay: NotificationEvent[],
  pages: NotificationEvent[][],
  compareDesc: (a: NotificationEvent, b: NotificationEvent) => number,
  max: number = MAX_FEED_ROWS,
): NotificationEvent[] {
  const seen = new Set<string>();
  const out: NotificationEvent[] = [];

  let oi = 0;
  // Walk the pages as one flattened stream without materialising it.
  let pi = 0;
  let pj = 0;
  const peekPage = (): NotificationEvent | undefined => {
    while (pi < pages.length) {
      const page = pages[pi];
      if (pj < page.length) return page[pj];
      pi += 1;
      pj = 0;
    }
    return undefined;
  };

  while (out.length < max) {
    const o = overlay[oi];
    const p = peekPage();
    if (o === undefined && p === undefined) break;

    let pick: NotificationEvent;
    if (o === undefined) {
      pick = p as NotificationEvent;
      pj += 1;
    } else if (p === undefined || compareDesc(o, p) <= 0) {
      // `<= 0` ⇒ the overlay event sorts first (newer or equal); on a tie
      // the overlay copy is chosen so it wins the dedupe below.
      pick = o;
      oi += 1;
    } else {
      pick = p;
      pj += 1;
    }

    if (!seen.has(pick.id)) {
      seen.add(pick.id);
      out.push(pick);
    }
  }

  return out;
}
